import type { AnalyticsActivitySummary, AnalyticsPoint, AnalyticsResolution } from "@queueryte/contracts";

export type AnalyticsRange = "businessDay" | "24h" | "7d" | "30d";
export type TicketActivityMode = "grouped" | "cumulative";
export type DeltaMode = "relative" | "percentagePoints";

export const analyticsRanges: ReadonlyArray<{ value: AnalyticsRange; label: string; milliseconds: number | null }> = [
  { value: "businessDay", label: "Business day", milliseconds: null },
  { value: "24h", label: "24 hours", milliseconds: 24 * 3_600_000 },
  { value: "7d", label: "7 days", milliseconds: 7 * 86_400_000 },
  { value: "30d", label: "30 days", milliseconds: 30 * 86_400_000 },
];

export interface AnalyticsSummary {
  ticketsIssued: number;
  ticketsCompleted: number;
  completionRate: number;
  averageWaitSeconds: number;
  averageProcessingSeconds: number;
  peakWaitingCount: number;
  activePercentage: number;
}

export interface MetricDelta {
  label: string;
  direction: "up" | "down" | "same";
}

export interface TimelineWindow {
  start: number;
  end: number;
  size: number;
  maxOffset: number;
}

function addScope(search:URLSearchParams,siteId?:string,queueId?:string):void {
  if(siteId)search.set("siteId",siteId);
  if(queueId)search.set("queueId",queueId);
}

export function analyticsUrl(range: AnalyticsRange, now = Date.now(), siteId?:string, queueId?:string): string {
  const search=new URLSearchParams();
  addScope(search,siteId,queueId);
  if (range === "businessDay") search.set("range","business-day");
  else {
    const selectedRange = analyticsRanges.find((option) => option.value === range);
    const duration = selectedRange?.milliseconds ?? 7 * 86_400_000;
    search.set("from",new Date(now-duration).toISOString());
    search.set("to",new Date(now).toISOString());
  }
  return `/api/v1/analytics?${search.toString()}`;
}

export function comparisonAnalyticsUrl(range: AnalyticsRange, now = Date.now(), siteId?:string, queueId?:string): string | null {
  if (range === "businessDay") return null;
  const duration = analyticsRanges.find((option) => option.value === range)?.milliseconds;
  if (duration === null || duration === undefined) return null;
  const search = new URLSearchParams({
    from: new Date(now - 2 * duration).toISOString(),
    to: new Date(now - duration).toISOString(),
  });
  addScope(search,siteId,queueId);
  return `/api/v1/analytics?${search.toString()}`;
}

export function ticketListUrl(
  metric:string,
  range:AnalyticsRange,
  now=Date.now(),
  siteId?:string,
  queueId?:string,
  cursor?:string|null,
  businessSessionId?:string|null,
  bucketStart?:string|null,
  resolution:AnalyticsResolution="1h",
):string {
  const search=new URLSearchParams({metric,limit:"50"});
  addScope(search,siteId,queueId);
  if(cursor)search.set("cursor",cursor);
  if(bucketStart){
    const start=Date.parse(bucketStart);
    const duration=resolution==="15m"?900_000:resolution==="1d"?86_400_000:3_600_000;
    if(Number.isFinite(start)){
      search.set("from",new Date(start).toISOString());
      search.set("to",new Date(start+duration).toISOString());
    }
  }else if(businessSessionId)search.set("businessSessionId",businessSessionId);
  else{
    const duration=analyticsRanges.find((option)=>option.value===range)?.milliseconds??24*3_600_000;
    search.set("from",new Date(now-duration).toISOString());
    search.set("to",new Date(now).toISOString());
  }
  return `/api/v1/tickets?${search.toString()}`;
}

export function summarizeAnalytics(points: AnalyticsPoint[], activitySummary?: AnalyticsActivitySummary | null): AnalyticsSummary {
  const totals = points.reduce((summary, point) => ({
    ticketsIssued: summary.ticketsIssued + point.ticketsIssued,
    ticketsCompleted: summary.ticketsCompleted + point.ticketsCompleted,
    waitSeconds: summary.waitSeconds + point.waitSecondsTotal,
    waitSamples: summary.waitSamples + point.waitSampleCount,
    processingSeconds: summary.processingSeconds + point.processingSecondsTotal,
    processingSamples: summary.processingSamples + point.processingSampleCount,
    peakWaitingCount: Math.max(summary.peakWaitingCount, point.peakWaitingCount),
    activeSeconds: summary.activeSeconds + point.activeSeconds,
    activeCapacitySeconds: summary.activeCapacitySeconds + point.activeCapacitySeconds,
  }), {
    ticketsIssued: 0,
    ticketsCompleted: 0,
    waitSeconds: 0,
    waitSamples: 0,
    processingSeconds: 0,
    processingSamples: 0,
    peakWaitingCount: 0,
    activeSeconds: 0,
    activeCapacitySeconds: 0,
  });

  return {
    ticketsIssued: activitySummary?.ticketsIssued ?? totals.ticketsIssued,
    ticketsCompleted: activitySummary?.ticketsCompleted ?? totals.ticketsCompleted,
    completionRate: activitySummary?.completionRate ?? (totals.ticketsIssued === 0 ? 0 : Math.min(100, Math.round(totals.ticketsCompleted / totals.ticketsIssued * 100))),
    averageWaitSeconds: totals.waitSamples === 0 ? 0 : Math.round(totals.waitSeconds / totals.waitSamples),
    averageProcessingSeconds: totals.processingSamples === 0 ? 0 : Math.round(totals.processingSeconds / totals.processingSamples),
    peakWaitingCount: activitySummary?.peakWaitingCount ?? totals.peakWaitingCount,
    activePercentage: activitySummary?.activePercentage ?? (totals.activeCapacitySeconds === 0 ? 0 : Math.min(100, Math.round(totals.activeSeconds / totals.activeCapacitySeconds * 100))),
  };
}

export function comparisonDelta(current: number, previous: number, mode: DeltaMode): MetricDelta {
  const difference = current - previous;
  const direction = difference === 0 ? "same" : difference > 0 ? "up" : "down";
  if (mode === "percentagePoints") {
    const rounded = Math.round(Math.abs(difference));
    return { label: difference === 0 ? "No change" : `${difference > 0 ? "+" : "-"}${rounded} pp`, direction };
  }
  if (previous === 0) {
    return current === 0 ? { label: "No change", direction: "same" } : { label: `+${Math.round(current)} new`, direction: "up" };
  }
  const percentage = Math.round(Math.abs(difference / previous) * 100);
  return { label: difference === 0 ? "No change" : `${difference > 0 ? "+" : "-"}${percentage}%`, direction };
}

export function roomActivePercentage(point: AnalyticsPoint): number {
  return point.activeCapacitySeconds === 0 ? 0 : Math.min(100, Math.round(point.activeSeconds / point.activeCapacitySeconds * 100));
}

export function ticketActivity(points: AnalyticsPoint[], mode: TicketActivityMode): Array<{ bucketStart: string; issued: number; completed: number }> {
  let issued = 0;
  let completed = 0;
  return points.map((point) => {
    issued = mode === "cumulative" ? issued + point.ticketsIssued : point.ticketsIssued;
    completed = mode === "cumulative" ? completed + point.ticketsCompleted : point.ticketsCompleted;
    return { bucketStart: point.bucketStart, issued, completed };
  });
}

export function timelineWindow(total: number, zoom: number, offset: number): TimelineWindow {
  if (total <= 0) return { start: 0, end: 0, size: 0, maxOffset: 0 };
  const safeZoom = Math.min(4, Math.max(1, zoom));
  const size = Math.max(1, Math.ceil(total / safeZoom));
  const maxOffset = Math.max(0, total - size);
  const start = Math.min(maxOffset, Math.max(0, Math.round(offset)));
  return { start, end: Math.min(total, start + size), size, maxOffset };
}

export function bucketLabel(bucketStart: string, range: AnalyticsRange): string {
  const value = new Date(bucketStart);
  if (range === "businessDay" || range === "24h") return value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  return value.toLocaleDateString([], { day: "numeric", month: "short" });
}

export function axisLabelIndexes(total: number, maximumLabels = 6): number[] {
  if (total <= 0) return [];
  if (total === 1) return [0];
  const every = Math.max(1, Math.ceil(total / maximumLabels));
  const indexes: number[] = [];
  for (let index = 0; index < total - 1; index += every) indexes.push(index);
  return indexes;
}

export function formatDuration(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) return "-";
  if (value < 60) return `${Math.round(value)} sec`;
  const minutes = Math.floor(value / 60);
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function readAnalyticsRange(storage: Pick<Storage, "getItem"> | null): AnalyticsRange {
  try {
    const value = storage?.getItem("queueryte.analytics.range");
    return analyticsRanges.some((option) => option.value === value) ? value as AnalyticsRange : "businessDay";
  } catch {
    return "businessDay";
  }
}

export function readTicketActivityMode(storage: Pick<Storage, "getItem"> | null): TicketActivityMode {
  try {
    return storage?.getItem("queueryte.analytics.activityMode") === "cumulative" ? "cumulative" : "grouped";
  } catch {
    return "grouped";
  }
}
